import React, { Fragment } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { ICompanyProviders } from 'store/companies/companiesTypes';
import Modal from 'components/Modal';
import ItemPicker from 'components/ItemPicker';
import QuestionPicker from '../QuestionPicker';
import { getTags } from '../GeneralQuestion/GeneralQuestionEditor';
import cloneDeep from 'lodash/cloneDeep';
import notify from '../../../../../Notify';
import {
  IQuestionsView,
  IReportView,
  ISurveyItem,
  ISurveyItemWithData,
  ISurveyReports,
  ISurveys,
  ISurveyView,
  QuestionTypes,
} from 'store/survey/surveyTypes';
import { appDispatch, RootState } from 'store/store';
import { IRelatedProviderItem } from 'store/providers/providerTypes';
import { IItemPicker } from 'store/entity/types';
import {
  addQuestion,
  getReportByIds,
  getReportByIdsFn,
  loadSurvey,
  loadSurveyFn,
  requireSurvey,
  updateSurveyReport,
} from 'store/survey/surveySlice';
import {
  requireDocument,
  requireDocumentFn,
  requireDocumentInFolder,
  requireDocumentInFolderFn,
  updateSurveyReportFn,
} from 'store/providers/providerSlice';
import { notifyError } from '../../../../../Notify/Notify';
import { WithTranslation, withTranslation } from 'react-i18next';

interface IDispatchToProps {
  getReportByIds: (reportIds: number[]) => ReturnType<typeof getReportByIdsFn>;
  loadSurvey: (surveyItem: ISurveyItem) => ReturnType<typeof loadSurveyFn>;
  requireSingleDocument: (
    documentId: number
  ) => ReturnType<typeof requireDocumentFn>;
  requireDocumentInFolder: (
    folderId: number,
    providerId: number
  ) => ReturnType<typeof requireDocumentInFolderFn>;
  updateSurveyReport: (
    reportItem: IReportView
  ) => ReturnType<typeof updateSurveyReportFn>;
  dispatch: (action: any) => any;
}

interface IStateToProps {
  providerItems: any;
  reports: ISurveyReports;
  surveys: ISurveys['data'];
  searchTermItems: any;
}

interface IOwnProps {
  itemId: number;
  targetSurvey: ISurveyView;
  targetItem: ISurveyItemWithData;
  providerId: number;
  onClose: () => void;
  providers: ICompanyProviders;
  allowedQuestionTypes?: QuestionTypes[];
  rootFolderId?: number;
}

type IComponentProps = IOwnProps &
  IDispatchToProps &
  IStateToProps &
  WithTranslation;

interface IComponentStates {
  loading: boolean;
  providerId: number;
  selectedSurvey?: number;
  saving: boolean;
  selectedProviderItem: IRelatedProviderItem | null;
  selectedQuestionIds: number[];
  view: 'files' | 'questions';
}

class QuestionExplorerModal extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  modalTabs = Object.values(this.props?.providers || {}).reduce(
    (temp, provider) => {
      temp.push({ id: provider.id, title: provider.name });
      return temp;
    },
    [] as Array<{ id: number; title: string }>
  );

  state: IComponentStates = {
    loading: false,
    providerId: this.modalTabs[0].id,
    saving: false,
    selectedProviderItem: null,
    selectedQuestionIds: [],
    view: 'files',
  };

  isRootFolderLoaded = () => {
    const { providerItems, providers, rootFolderId } = this.props;
    return (
      providerItems[this.state.providerId].folders[
        rootFolderId || providers[this.state.providerId].rootDocumentId
      ]?.state === 'loaded'
    );
  };

  handleRequireDocumentsInFolder = (requiredFolderId: number) => {
    return this.props.requireDocumentInFolder(
      requiredFolderId,
      this.state.providerId
    );
  };

  handleRequireFolderAndItems = (requiredFolderId: number) => {
    const dadItemId =
      this.props.providerItems[this.state.providerId].items[requiredFolderId];
    if (dadItemId) {
      return Promise.resolve();
    } else {
      return Promise.all([
        this.props.requireSingleDocument(requiredFolderId),
        this.handleRequireDocumentsInFolder(requiredFolderId),
      ]);
    }
  };

  handleSelectATab = (providerId: number | string) => {
    this.setState({ providerId: providerId as number, loading: true });
  };

  onChangeProviderItem = (providerItem: IRelatedProviderItem) => {
    if (providerItem) {
      const { type } = providerItem;
      if (type === 'surveyReport' || type === 'survey') {
        this.setState(
          { selectedProviderItem: providerItem },
          this.handleChangeStep
        );
      }
    } else {
      this.setState({ selectedProviderItem: providerItem });
    }
  };

  onChangeReportItem = (reportId: number[]) => {
    this.setState({ selectedQuestionIds: reportId });
  };

  handleChangeStep = () => {
    this.setState((prevState) => ({
      view: prevState.view === 'questions' ? 'files' : 'questions',
    }));
    if (this.state.selectedProviderItem && this.state.view === 'files') {
      this.setState({ loading: true });
      const item =
        this.props.providerItems[this.state.selectedProviderItem.providerId]
          .items[this.state.selectedProviderItem.id].data;
      if (item.type === 'survey') {
        const surveyItem = item;
        this.props.loadSurvey(surveyItem).then(
          () => {
            this.setState({
              loading: false,
              selectedSurvey: surveyItem.surveyId,
            });
          },
          (e) => {
            notifyError(e);
          }
        );
      }
    } else if (this.state.view === 'questions') {
      this.setState({ selectedQuestionIds: [], selectedProviderItem: null });
    }
  };

  componentDidMount() {
    if (!this.isRootFolderLoaded()) {
      this.handleRequireDocumentsInFolder(
        this.props.rootFolderId ||
          this.props.providers[this.props.providerId].rootDocumentId
      );
    }
  }

  render() {
    const { onClose, providerItems, providers, surveys } = this.props;
    const { selectedProviderItem, loading, providerId, selectedSurvey } =
      this.state;

    const isRootFolderLoaded = this.isRootFolderLoaded();

    const survey = selectedSurvey ? surveys[selectedSurvey] : undefined;
    const currentSelected = [] as number[];
    Object.values(this.props.targetSurvey.questionMap || {}).forEach(
      (inTargetQuestion) => {
        if (survey && survey.data.questionMap?.[inTargetQuestion.data.id])
          if (
            survey.data.questionMap?.[inTargetQuestion.data.id].data.title ===
            inTargetQuestion.data.title
          )
            currentSelected.push(inTargetQuestion.data.id);
      }
    );

    let selectedItem: IItemPicker | null = null;
    let relatedProviderItem: any;
    if (
      selectedProviderItem &&
      isRootFolderLoaded &&
      selectedProviderItem.providerId === providerId
    ) {
      /*
        third condition check that item belongs to current provider or not on switch tab
      */
      relatedProviderItem =
        providerItems[providerId].items[selectedProviderItem.id].data;
      selectedItem = {
        id: relatedProviderItem.id,
        name: relatedProviderItem.name,
        dadId: relatedProviderItem.dadId,
        type: 'surveyReport',
      };
    }
    let questions: IQuestionsView[] = [];
    if (survey) {
      questions = Object.values(survey.data.questionMap || {})
        .filter((it) => !it.data.deleted)
        .map((it) => it.data);
    }

    const handleSaveQuestions = async () => {
      // todo add selected question to survey
      const maxOrder = Object.values(
        this.props.targetSurvey.questionMap!
      ).reduce((current, item) => {
        return Math.max(current, item.data.order);
      }, Number.MIN_VALUE);
      this.setState({ saving: true });
      const toAdd = cloneDeep(this.state.selectedQuestionIds);
      for (
        let index = 0;
        index < this.state.selectedQuestionIds.length;
        index++
      ) {
        const it = this.state.selectedQuestionIds[index];
        const question = cloneDeep(survey!.data.questionMap![it]);
        question.data.order = maxOrder + index;
        try {
          await this.props
            .dispatch(
              addQuestion({
                questionData: question.data,
                providerSurveyData: this.props.targetItem.data,
                keepQuestionId: true,
              })
            )
            .then(
              () => {
                toAdd.splice(toAdd.indexOf(question.data.id), 1);
              },
              () => {
                notify(
                  this.props.t('errorAddingQuestion', {
                    title: question.data.title,
                  }),
                  'warning'
                );
              }
            );
        } catch (e) {
          /*continue */
        }
      }
      this.setState({ selectedQuestionIds: toAdd });
      this.setState({ saving: false });
    };
    return (
      <Modal
        width="50%"
        tabs={this.modalTabs}
        title={this.props.t('addReport')}
        onClose={onClose}
        isActive={true}
        selectedTab={providerId}
        onSelectATab={this.handleSelectATab}
      >
        <Fragment>
          {isRootFolderLoaded ? (
            <div
              style={{
                height: 'calc(100% - 110px)',
                display: this.state.view === 'files' && !loading ? '' : 'none',
              }}
            >
              <ItemPicker
                providerId={providerId}
                saveTitle={this.props.t('nextStep')}
                onSaveItem={this.onChangeProviderItem}
                providerItems={providerItems[providerId].items}
                rootFolder={{
                  id:
                    this.props.rootFolderId ||
                    providers[providerId]?.rootDocumentId,
                  name: this.props.rootFolderId
                    ? providerItems[providerId].items[this.props.rootFolderId]
                        ?.data?.name || ''
                    : this.props.t('rootFolderName', {
                        folderName: providers[providerId]?.name || '',
                      }),
                  dadId: -1,
                  type: 'folder',
                }}
                selectedProviderItem={selectedItem}
                onRequireFolderAndItems={this.handleRequireFolderAndItems}
                onRequireDocumentsInFolder={this.handleRequireDocumentsInFolder}
              />
            </div>
          ) : (
            <p style={{ alignSelf: 'center', textAlign: 'center' }}>
              {this.props.t('loading')}
            </p>
          )}
        </Fragment>

        {this.state.view !== 'files' && (
          <Fragment>
            {loading ? (
              <p style={{ alignSelf: 'center', textAlign: 'center' }}>
                {this.props.t('loading')}
              </p>
            ) : (
              <QuestionPicker
                onChangeView={this.handleChangeStep}
                onSaveQuestions={handleSaveQuestions}
                saving={this.state.saving}
                showBackButton={this.state.view === 'questions'}
                onSelectQuestions={this.onChangeReportItem}
                questions={questions}
                allowedQuestionTypes={this.props.allowedQuestionTypes}
                currentSelected={currentSelected}
                selectedQuestions={this.state.selectedQuestionIds}
                itemName={survey?.data.name || ''}
              />
            )}
          </Fragment>
        )}
      </Modal>
    );
  }
}

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    requireSingleDocument: (documentId: number) =>
      appDispatch(requireDocument({ documentId })),
    requireDocumentInFolder: (folderId: number, providerId: number) =>
      appDispatch(requireDocumentInFolder({ folderId, providerId })),
    getReportByIds: (reportIds: number[]) =>
      appDispatch(getReportByIds({ reportIds })),
    updateSurveyReport: (reportItem: IReportView) =>
      appDispatch(updateSurveyReport({ report: reportItem })),
    requireSurvey: (surveyItem: ISurveyItem) =>
      appDispatch(requireSurvey({ providerSurveyItemData: surveyItem })),
    loadSurvey: (surveyItem: ISurveyItem) =>
      appDispatch(loadSurvey({ providerSurveyItemData: surveyItem })),
    dispatch: appDispatch,
  };
};

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const providerItems = state.providers.items;
  const reports = state.survey.reports;
  const surveys = state.survey.data;
  const allTags = state.providers.tags[props.providerId!]!.data;
  const tagOptions = getTags(allTags, true);
  const searchTermItems = state.providers.items[props.providerId].search;
  return {
    providerItems,
    reports,
    surveys,
    tagOptions,
    searchTermItems,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation('modals')(QuestionExplorerModal));
